import { Box, Flex, Text } from '@radix-ui/themes';
import { useMediaState, useSliderState } from '@vidstack/react';
import React from 'react';
import { useAuthToken } from '@features/authentication';
import { useSmartPlayerContext } from '../context/SmartPlayerContext';
import { formatTimecode } from '../utils/timecode';
import { Track } from '@features/Clip/types/media';
import { useClampedPosition } from '@features/advancedVideoPlayer/hooks';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import sharedStyles from '../styles/SmartPlayer.module.css';
import styles from './SmartPlayerPreview.module.css';

interface SmartPlayerPreviewProps {
    tracks: Track[];
    aspectRatio?: number;
    sliderRect?: DOMRect;
    isHovered: boolean;
}

export const SmartPlayerPreview = ({
    tracks,
    aspectRatio = 16 / 9,
    sliderRect,
    isHovered,
}: SmartPlayerPreviewProps) => {
    const { t } = useTranslation('smartPlayer');
    const token = useAuthToken();
    const { fps } = useSmartPlayerContext();

    const pointerPercent = useSliderState('pointerPercent');
    const duration = useMediaState('duration');

    const totalFrames = Math.round(duration * fps);
    const textFrame = Math.floor((pointerPercent / 100) * totalFrames);
    const timeInSeconds = textFrame / fps;

    const backendTotalFrames = React.useMemo(() => {
        if (!tracks || tracks.length === 0) return 0;
        return Math.max(...tracks.map((t) => t.end));
    }, [tracks]);

    const imageFrame =
        backendTotalFrames > 0
            ? Math.min(
                  Math.floor((pointerPercent / 100) * backendTotalFrames),
                  backendTotalFrames - 1,
              )
            : textFrame;

    const pointerX = sliderRect ? (pointerPercent / 100) * sliderRect.width : 0;
    const { clampedStyle, relativePointerX } = useClampedPosition({
        position: { x: pointerX },
        containerRect: sliderRect,
        size: { width: 160, height: 0 },
        edgeBleed: 8,
    });

    const track = React.useMemo(
        () => tracks.find((t) => imageFrame >= t.start && imageFrame < t.end),
        [tracks, imageFrame],
    );

    const tileStyles = React.useMemo(() => {
        if (!track) return undefined;
        const index = Math.floor(
            (track.stitchCount * (imageFrame - track.start)) /
                (track.end - track.start),
        );
        const posX =
            track.stitchX > 1
                ? ((index % track.stitchX) * 100) / (track.stitchX - 1)
                : 0;
        const posY =
            track.stitchY > 1
                ? (Math.floor(index / track.stitchX) * 100) /
                  (track.stitchY - 1)
                : 0;

        return {
            '--stitch-x': track.stitchX,
            '--stitch-y': track.stitchY,
            backgroundImage: `url(${track.src}?access_token=${token})`,
            backgroundPosition: `${posX.toFixed(2)}% ${posY.toFixed(2)}%`,
            backgroundSize: `${track.stitchX * 100}% ${track.stitchY * 100}%`,
            aspectRatio: aspectRatio,
        } as React.CSSProperties;
    }, [aspectRatio, imageFrame, token, track]);

    if (!sliderRect) return null;

    return (
        <Box
            style={{
                ...clampedStyle,
                bottom: 'calc(100% + 12px)',
                zIndex: 10,
                opacity: isHovered ? 1 : 0,
                transition: 'opacity 0.2s ease',
                pointerEvents: 'none',
            }}
        >
            <Flex
                direction={'column'}
                className={classNames(
                    styles.previewContainer,
                    sharedStyles.solidPanel,
                )}
            >
                {track ? (
                    <Box className={styles.mosaicTile} style={tileStyles} />
                ) : (
                    <Box className={styles.invalidMosaicTile} p={'4'}>
                        <Text
                            align={'center'}
                            size='2'
                            style={{ color: 'var(--gray-11)' }}
                        >
                            {t('preview.not_available')}
                        </Text>
                    </Box>
                )}

                <Box p='1' className={styles.labelArea}>
                    <Text
                        size='1'
                        weight='bold'
                        style={{ color: 'var(--gray-12)' }}
                    >
                        {formatTimecode(timeInSeconds, fps)}
                    </Text>
                </Box>

                <Box
                    className={styles.previewIndicator}
                    style={{ left: `${relativePointerX}px` }}
                />
            </Flex>
        </Box>
    );
};
